import React, { Fragment, useEffect, useMemo, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

// Components

import AdditionalInformationSummary from './pageIncludes/summary/AdditionalInformationSummary';
import AttestationsSummary from './pageIncludes/summary/AttestationsSummary';
import ComparisonSwitcher from './pageIncludes/summary/ComparisonSwitcher';
import FamilyAndHouseholdSummary from './pageIncludes/summary/FamilyAndHouseholdSummary';
import FamilyAndHouseholdSummary_MN from './pageIncludes/summary/FamilyAndHouseholdSummary_MN';
import HouseholdCommonSummary from './pageIncludes/summary/HouseholdCommonSummary';
import IncomeInformationSummary from './pageIncludes/summary/IncomeInformationSummary';
import PageHeader from '../headers/PageHeader';
import PrimaryContactPersonSummary from './pageIncludes/summary/PrimaryContactPersonSummary';
import SsapSummaryErrorBoundary from '@components/ssap/SsapSummaryErrorBoundary';

// ActionCreators

import {
    clearMethods,
    clearPreviousData,
    clearSsapSummary,
    fetchAllBloodRelationships,
    fetchEnrollments,
    fetchPreviousApplication,
    fetchTribes,
    sendSummaryToInbox,
    setMethods,
    showExtraFinalSummary,
    toggleUpdatedFieldsVisibility
} from '@actions/ssap/ssapActionCreators';

import {
    fetchCommunicationPreferences
} from '@actions/individualPortal/preferences/communicationPreferencesActionCreators';

// Selectors

import {
    selectIfExtraFinalSummaryShown,
    selectIfFinalReviewPageActive,
    selectIfOnlyUpdatedFieldsShown,
    selectSsapPreviousData,
    selectIfPreviousDataLoaded,
    selectIfSummaryCrashed,
    selectIfShouldFetchEnrollments,
    selectIfRenewalFlow,
    selectIfSsapOpenInReviewMode,
    selectExchangeAndProgramNames,
    selectAddressVisibilityDueToGeneralDelivery,
    selectIfSogiFeatureEnabled
} from '@reducers/appReducer';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { formatDate } from '@utils/dates/date.utils';

// Constants

import { configs } from '@constants/configs/configs';
import { flowTypes as FLOW_TYPES } from '@constants/ssap/flows/flowTypes';
import { PAGES } from '@constants/ssap/flows/pagesTypes';
import { DESC_FORMAT_12 } from '@utils/dates/dateFormats.utils';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - Summary page for 'Sign and Review' Section
 *
 */
const Page3_16 = props => {

    const {
        actions: {
            clearMethods,
            fetchAllBloodRelationships,
            fetchEnrollments,
            fetchPreviousApplication,
            fetchCommunicationPreferences,
            fetchTribes,
            sendSummaryToInbox,
            setMethods
        },
        data,
        data: {
            applicationGuid,
            applicationSignatureDate,
            coverageYear
        },
        environment: {
            assets: {
                LOGO
            },
            exchangeName,
            isState,
            language,
            localDates: {
                serverDate
            }
        },
        flow: {
            flowType
        },
        formName,
        adjustedFlow,
        allStatuses,
        taxHousehold,
        householdMembers,
        messages,
        relations,
        relations: {
            loadedRelationsWholeList = []
        },
        stateCode,
        tribes
    } = props;

    // Props from PagesContainer
    const {
        alwaysDisplayMedicaidQuestions,
        inlineCurrentIncomeEnabled,
        medicaidTerminationFeatureEnabled
    } = props;

    const areOnlyUpdatedFieldsShown = useSelector(selectIfOnlyUpdatedFieldsShown);
    const isExtraSummaryPageShown = useSelector(selectIfExtraFinalSummaryShown);
    const isFinalReviewPageActive = useSelector(selectIfFinalReviewPageActive);
    const isPreviousSsapApplicationLoaded = useSelector(selectIfPreviousDataLoaded);
    const isSummaryCrashed = useSelector(selectIfSummaryCrashed);
    const previousData = useSelector(selectSsapPreviousData);
    const shouldFetchEnrollments = useSelector(selectIfShouldFetchEnrollments);
    const isRenewalFlow = useSelector(selectIfRenewalFlow);
    const isSsapInReviewMode = useSelector(selectIfSsapOpenInReviewMode);
    const exchangeAndProgramNames = useSelector(selectExchangeAndProgramNames);
    const addressVisibilityDueToGeneralDelivery = useSelector(selectAddressVisibilityDueToGeneralDelivery);
    const isSogiFeatureEnabled = useSelector(selectIfSogiFeatureEnabled);

    const {
        applicantGuid: pcpApplicantGuid,
        name,
    } = householdMemberTools.getPrimaryContact(householdMembers);

    const {
        buttonContainer: ButtonContainer,
        isUnemploymentIncomeAttestationEnabled,
        itfFeatureEnabled,
        pageName
    } = props;

    const [ container, setContainer ] = useState(null);

    const [ print, togglePrinting ] = useState({
        action: null,
        isComparisonToggled: false,
        isPrinting: false,
        withDiffs: isExtraSummaryPageShown
    });

    const isExtraFinalSummaryPage  = pageName === PAGES.EXTRA_FINAL_APPLICATION_REVIEW_PAGE;

    const intlApi = useIntl();


    useEffect(() => {

        const PDF_HEADER_WITH_STATE_LOGO = (
            <div className='pdf-header'>
                <img
                    src={ LOGO }
                    alt='state_logo'
                />
                <h2> { exchangeName } </h2>
                <p>{ intlApi.formatMessage({ id: 'ssap.pdf.extracted' }, { exchangeName }) }</p>
                <div>
                    <p className='float-left'>
                        {
                            intlApi.formatMessage({ id: 'ssap.pdf.case_number' }, { caseNumber: applicationGuid })
                        }
                    </p>
                    <p className='float-right'>
                        {
                            intlApi.formatMessage(
                                { id: 'ssap.pdf.submission_date' },
                                {
                                    submissionDate:
                                        applicationSignatureDate ?
                                            formatDate(applicationSignatureDate, DESC_FORMAT_12) :
                                            intlApi.formatMessage({ id: 'ssap.pdf.ssap_not_submitted_yet' })
                                }
                            )

                        }
                    </p>
                    <div/>
                </div>
            </div>
        );

        const script = document.createElement('script');

        (() => {
            script.type = 'application/javascript';
            script.src = configs.endpoints.HTML_2_PDF_SCRIPT_ENDPOINT;
            document.head.appendChild(script);
        })();

        const printConfig = {
            ['.buttons-group']: {
                display: 'none'
            },
            ['h3']: {
                color: '#333333',
            },
            ['.header-btn']: {
                visibility: 'hidden'
            },
            ['.gi-alert__print_hidden']: {
                display: 'none'
            }
        };

        const removeScript = () => {
            document.head.removeChild(script);
        };

        const payload = document.createElement('div');

        const headerEligibleStates = [
            configs.states.ILLINOIS,
            configs.states.MINNESOTA,
            configs.states.NEVADA,
            configs.states.NEW_JERSEY,
            configs.states.NEW_MEXICO,
            configs.states.PENNSYLVANIA
        ];

        Promise.resolve(
            headerEligibleStates.includes(stateCode)
                ? (() => {
                    const headerHtml = ReactDOMServer.renderToStaticMarkup(PDF_HEADER_WITH_STATE_LOGO);
                    const headerWrapper = document.createElement('div');
                    headerWrapper.innerHTML = headerHtml;
                    payload.prepend(headerWrapper);
                })()
                : payload       )
            .then(() => {
                [
                    '.extract',
                    '.extract2',
                    '.extract3'
                ].forEach(contentChunk => {
                    (
                        el => el
                            ? payload.appendChild(el.cloneNode(true))
                            : null
                    )(document.querySelector(contentChunk));
                });

                Object
                    .keys(printConfig)
                    .map(domElement => [...payload.querySelectorAll(domElement)])
                    .reduce((commonList, el) => [ ...commonList, ...el ], [])
                    .forEach(selected => {

                        const tagSelector = selected.nodeName.toLowerCase();

                        const classSelector = (className => className ? `.${className}` : undefined)(selected.className.split(' ').find(cl => Object.keys(printConfig).some(sel => String(sel).slice(1) === cl)));

                        const idSelector = Object.keys(printConfig).find(sel => String(sel).slice(1) === selected.id);

                        const commonStyleObj = [
                            printConfig[tagSelector],
                            printConfig[classSelector],
                            printConfig[idSelector]
                        ].filter(sel => sel)
                            .reduce(( commonStyleObj, styleObj ) => ({ ...commonStyleObj, ...styleObj }), {});

                        if(commonStyleObj) {
                            Object.entries(commonStyleObj).forEach(([cssProp, cssValue]) => {
                                selected.style[cssProp] = cssValue;
                            });
                        }
                    });
            })
            .then(() => {
                setContainer(payload);
            });

        return () => {
            removeScript();
        };

    }, [
        tribes.length,
        messages[language],
        isPreviousSsapApplicationLoaded,
        areOnlyUpdatedFieldsShown,
        isExtraSummaryPageShown
    ]);

    useEffect(() => {
        isState.ID && fetchCommunicationPreferences();
        loadedRelationsWholeList.length === 0 && fetchAllBloodRelationships();
        tribes.length === 0 && fetchTribes();
        shouldFetchEnrollments && fetchEnrollments();
        !isPreviousSsapApplicationLoaded
            && isFinalReviewPageActive
            && isExtraSummaryPageShown
            && fetchPreviousApplication(pcpApplicantGuid);
    }, []);

    useEffect(() => {
        return () => {
            if(isFinalReviewPageActive) {
                props.actions.clearSsapSummary();
                props.actions.clearPreviousData();
            }
        };
    }, [ ]);


    /**
     * Function that generates PDF for printing or saving
     *
     * @param {string} action - type of action to do after PDF generation has been finished
     */
    const handleAction = action => {


        const opt = {
            margin:       [.3, .5, .3, .5],
            filename:     `${normalizeNameOfPerson(name)} - Summary.pdf`,
            image:        { type: 'jpeg', quality: 0.98 },
            html2canvas:  { scale: 2 },
            jsPDF:        { unit: 'in', format: 'a4' },
            pagebreak:    { avoid: ['span', 'div'], before: ['.break_before'], after: ['.break_after'] }

        };

        switch(action) {
            case 'download':
                 
                window.html2pdf().set(opt).from(container).output().save();

                isState.ID && sendSummaryToInbox();

                break;
            case 'print':
                 
                window.html2pdf().set(opt).from(container).output('bloburl').then(res => {
                    window.open(res, '_blank').print();
                });
                break;
        }
    };

    const handleSummaryOutput = (...args) => {
        const [ action ] = args;

        if(print.withDiffs) {
            props.actions.toggleUpdatedFieldsVisibility(false);
            props.actions.showExtraFinalSummary(false);
            setTimeout(() => {
                togglePrinting(print => ({
                    ...print,
                    action,
                    isComparisonToggled: areOnlyUpdatedFieldsShown,
                    isPrinting: true
                }));
            });
        } else {
            togglePrinting(print => ({
                ...print,
                action,
                isComparisonToggled: areOnlyUpdatedFieldsShown,
                isPrinting: true
            }));
        }
    };

    useEffect(() => {
        if(print.isPrinting && isExtraSummaryPageShown === false) {
            handleAction(print.action);

            togglePrinting(print => ({
                ...print,
                action: null,
                isComparisonToggled: false,
                isPrinting: false
            }));

            if(print.isComparisonToggled) {
                props.actions.toggleUpdatedFieldsVisibility(true);
            }

            if(print.withDiffs) {
                props.actions.showExtraFinalSummary(true);
            }
        }
    }, [ print.isPrinting, isExtraSummaryPageShown ]);

    useEffect(
        () => {
            setMethods(
                'Page3_16',
                {
                    handleSummaryOutput
                }
            );

            return () => {
                clearMethods('Page3_16');
            };
        },
        [
            loadedRelationsWholeList,
            container,
            areOnlyUpdatedFieldsShown,
            isExtraSummaryPageShown
        ]
    );

    const indexMapper = useMemo(
        () => {
            let mapping = {
                currentValues: {},
                previousValues: {}
            };

            return data.taxHousehold.householdMember.reduce(
                (mapping, hhm, idx) => {
                    const { applicantGuid } = hhm;
                    const matchedIndex = previousData.taxHousehold.householdMember.findIndex(hhmFromPreviousData => {
                        const { applicantGuid: hhmFromPreviousDataApplicantGuid } = hhmFromPreviousData;
                        return hhmFromPreviousDataApplicantGuid === applicantGuid;
                    });

                    const currentIndexAsString = String(idx);
                    const previousIndexAsString = String (~matchedIndex ? matchedIndex : -1);

                    return {
                        ...mapping,
                        currentValues: {
                            ...mapping.currentValues,
                            [ currentIndexAsString ]: currentIndexAsString
                        },
                        previousValues: {
                            ...mapping.previousValues,
                            [ currentIndexAsString ]: previousIndexAsString
                        }
                    };
                },
                mapping
            );
        },
        [ data, previousData ]
    );

    return (
        <SsapSummaryErrorBoundary>
            <div
                className = 'fade-in'
                id = 'rsReview'
            >
                <div className = 'subsection' >
                    <ComparisonSwitcher
                        { ...{
                            actions: {
                                toggleUpdatedFieldsVisibility: props.actions.toggleUpdatedFieldsVisibility
                            },
                            areOnlyUpdatedFieldsShown,
                            formName,
                            isExtraFinalSummaryPage,
                            isExtraSummaryPageShown,
                            isSummaryCrashed
                        } }
                    />
                    <div className = 'extract' >
                        <div className = 'final-review-heading' >
                            <PageHeader
                                text = 'ssap.subheader.household_and_demo_information'
                                headerClassName = 'gi-heading-alt summary__header--no-border'
                                { ...(
                                    !isExtraFinalSummaryPage
                                        ? {
                                            additionalElement: (
                                                <Fragment>
                                                    <ButtonContainer
                                                        args = { [ 'download' ] }
                                                        onClick = { handleSummaryOutput }
                                                        type = 'downloadButton'
                                                    />
                                                    <ButtonContainer
                                                        args = { [ 'print' ] }
                                                        onClick = { handleSummaryOutput }
                                                        type = 'printButton'
                                                    />
                                                </Fragment>
                                            ),
                                            additionalElementClassName: 'header-btn'
                                        }
                                        : {
                                            additionalElement: (
                                                <ButtonContainer
                                                    args = { [ undefined, PAGES.PRIMARY_CONTACT_INFORMATION_PAGE ] }
                                                    type = 'editButton_secondary'
                                                />
                                            ),
                                            additionalElementClassName: 'header-btn'
                                        }
                                ) }
                            />
                        </div>
                        <div className = 'final-review-body break_after' >
                            <PrimaryContactPersonSummary
                                { ...{
                                    indexMapper
                                } }
                            />
                        </div>

                        <div className = 'final-review-body break_after' >
                            <HouseholdCommonSummary
                                { ...{
                                    exchangeName,
                                    indexMapper
                                } }
                            />
                        </div>

                        {
                            isState.MN
                                ? (
                                    <Fragment>
                                        <div className = 'final-review-heading' >
                                            <PageHeader
                                                text = 'common.household_members'
                                                headerClassName = 'gi-heading-alt'
                                            />
                                        </div>
                                        <div className = 'final-review-body' >
                                            <FamilyAndHouseholdSummary_MN
                                                { ...{
                                                    buttonContainer: ButtonContainer,
                                                    householdMembers,
                                                    flowType,
                                                    stateCode
                                                } }
                                            />
                                        </div>
                                    </Fragment>
                                )
                                : (
                                    <FamilyAndHouseholdSummary
                                        { ...{
                                            addressVisibilityDueToGeneralDelivery,
                                            buttonContainer: ButtonContainer,
                                            allStatuses,
                                            exchangeAndProgramNames,
                                            indexMapper,
                                            isSogiFeatureEnabled,
                                            isState,
                                            medicaidTerminationFeatureEnabled,
                                            relations,
                                            taxHousehold,
                                            householdMembers,
                                            flowType,
                                            serverDate,
                                            stateCode,
                                            tribes
                                        } }
                                    />
                                )
                        }
                    </div>

                    {
                        !isState.MN && flowType === FLOW_TYPES.FINANCIAL_FLOW &&

                        <div className = 'extract2 break_before' >
                            <div className = 'final-review-body' >
                                <IncomeInformationSummary
                                    { ...{
                                        buttonContainer: ButtonContainer,
                                        coverageYear,
                                        householdMembers,
                                        isState,
                                        isUnemploymentIncomeAttestationEnabled,
                                        indexMapper,
                                        itfFeatureEnabled,
                                        pageName,
                                        previousData,
                                        taxHousehold
                                    } }
                                />
                            </div>
                            <div className = 'final-review-body' >
                                <AdditionalInformationSummary
                                    { ...{
                                        adjustedFlow,
                                        allStatuses,
                                        alwaysDisplayMedicaidQuestions,
                                        buttonContainer: ButtonContainer,
                                        householdMembers,
                                        indexMapper,
                                        inlineCurrentIncomeEnabled,
                                        isRenewalFlow,
                                        isState,
                                        itfFeatureEnabled,
                                        previousData,
                                        serverDate,
                                        taxHousehold
                                    } }
                                />
                            </div>
                        </div>
                    }

                    {
                        (isSsapInReviewMode && !isState.MN)
                            ? (
                                <div className = 'extract3' >
                                    <AttestationsSummary
                                        data = { data }
                                        exchangeName = { exchangeName }
                                        flowType = { flowType }
                                        isState = { isState }
                                        exchangeAndProgramNames={ exchangeAndProgramNames }
                                    />
                                </div>
                            )
                            : null
                    }
                </div>
            </div>
        </SsapSummaryErrorBoundary>
    );
};

const mapStateToProps = state => {

    const sortedHouseholds = state
        .ssap
        .data
        .taxHousehold
        .householdMember.sort((a, b) => a.personId - b.personId);

    return {
        messages: state.common.messages,

        adjustedFlow: state.ssap.adjustedFlow,
        data: state.ssap.data,
        environment: state.common.environment,
        flow: state.ssap.flow,
        householdMembers: sortedHouseholds,
        relations: state.ssap.relations,

        stateCode: state
            .common
            .environment
            .stateCode,

        taxHousehold: state
            .ssap
            .data
            .taxHousehold,

        tribes: state.ssap.tribes
    };
};

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        clearMethods,
        clearPreviousData,
        clearSsapSummary,
        fetchAllBloodRelationships,
        fetchEnrollments,
        fetchPreviousApplication,
        fetchCommunicationPreferences,
        fetchTribes,
        sendSummaryToInbox,
        setMethods,
        showExtraFinalSummary,
        toggleUpdatedFieldsVisibility
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page3_16);
